import React from 'react';
import { Lock, CheckCircle, AlertTriangle, X } from 'lucide-react';
import { FIELD_LABELS } from './fieldLabels';

/**
 * ConflictDrawer - Per-field conflict resolution UI
 * @param {Object} props
 * @param {Object} props.conflict - Conflict object with local/remote data
 * @param {Function} props.onResolve - Callback when conflict is resolved
 * @param {Function} props.onClose - Callback to close drawer
 */
export function ConflictDrawer({ conflict, onResolve, onClose }) {
  const { collection, local, remote, userManaged = [], engineManaged = [], appManaged = [] } = conflict;
  const labels = FIELD_LABELS[collection] || {};
  const fields = Array.from(new Set([
    ...Object.keys(local || {}), 
    ...Object.keys(remote || {}),
    ...userManaged, 
    ...engineManaged, 
    ...appManaged
  ]));

  // state of picks: { fieldName: 'local'|'remote'|'locked' }
  const [choice, setChoice] = React.useState(() => {
    const c = {};
    for (const f of fields) {
      if (appManaged.includes(f)) c[f] = 'locked';
      else if (userManaged.includes(f)) c[f] = 'local';
      else if (engineManaged.includes(f)) c[f] = 'remote';
      else c[f] = 'local'; // safe default
    }
    return c;
  });

  function buildResolved() {
    const out = { ...(local || {}) };
    for (const f of fields) {
      if (choice[f] === 'remote') out[f] = remote && remote[f];
      if (choice[f] === 'local') out[f] = local && local[f];
      // locked = leave as-is (local/app-managed)
    }
    return out;
  }

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  // Focus trap setup
  const drawerRef = React.useRef(null);

  React.useEffect(() => {
    if (drawerRef.current) {
      const firstButton = drawerRef.current.querySelector('button, input, [tabindex="0"]');
      if (firstButton) {
        firstButton.focus();
      }
    }
  }, []);

  const formatValue = (value) => {
    if (value === null || value === undefined) return '';
    if (typeof value === 'object') return JSON.stringify(value);
    return String(value);
  };

  const hasChanges = fields.some(f => {
    const localVal = formatValue(local && local[f]);
    const remoteVal = formatValue(remote && remote[f]);
    return localVal !== remoteVal;
  });

  return (
    <div className="fixed inset-0 bg-black/40 flex justify-end z-50">
      <div 
        ref={drawerRef}
        className="w-full max-w-4xl bg-white h-full flex flex-col"
        role="dialog"
        aria-modal="true"
        aria-labelledby="conflict-title"
      >
        {/* Header */}
        <header className="flex items-center justify-between p-6 border-b border-gray-200 bg-white">
          <div className="flex items-center gap-3">
            <AlertTriangle className="h-6 w-6 text-amber-600" />
            <div>
              <h2 id="conflict-title" className="text-xl font-semibold text-gray-900">
                Resolve Conflict
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                {collection} • {conflict.recordId} • {fields.length} fields to review
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            aria-label="Close conflict resolution"
          >
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </header>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {!hasChanges && (
            <div className="mb-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <div className="flex items-center gap-2 text-blue-800">
                <CheckCircle className="h-5 w-5" />
                <span className="font-medium">No conflicts detected</span>
              </div>
              <p className="text-blue-700 text-sm mt-1">
                Local and remote records are identical. You can safely apply without changes.
              </p>
            </div>
          )}

          {/* Desktop view */}
          <div className="hidden lg:block">
            <table className="w-full text-sm">
              <thead className="text-left text-gray-500 border-b border-gray-200">
                <tr>
                  <th className="py-3 pr-4 font-medium">Field</th>
                  <th className="py-3 pr-4 font-medium">Local Value</th>
                  <th className="py-3 pr-4 font-medium">Server Value</th>
                  <th className="py-3 font-medium">Resolution</th>
                </tr>
              </thead>
              <tbody>
                {fields.map(f => {
                  const localVal = formatValue(local && local[f]);
                  const remoteVal = formatValue(remote && remote[f]);
                  const isDifferent = localVal !== remoteVal;
                  
                  return (
                    <tr key={f} className={`border-t ${isDifferent ? 'bg-amber-50' : ''}`}>
                      <td className="py-3 pr-4">
                        <div className="flex items-center gap-2">
                          <span className="font-medium text-gray-900">{labels[f] || f}</span>
                          {isDifferent && <div className="w-2 h-2 bg-amber-500 rounded-full" />}
                        </div>
                        {appManaged.includes(f) && (
                          <span className="text-xs text-gray-500">App-managed</span>
                        )}
                        {userManaged.includes(f) && (
                          <span className="text-xs text-blue-600">User-managed</span>
                        )}
                        {engineManaged.includes(f) && (
                          <span className="text-xs text-green-600">Engine-managed</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <div className="max-w-xs">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all">
                            {localVal || <span className="text-gray-400">empty</span>}
                          </code>
                        </div>
                      </td>
                      <td className="py-3 pr-4">
                        <div className="max-w-xs">
                          <code className="text-xs bg-gray-100 px-2 py-1 rounded break-all">
                            {remoteVal || <span className="text-gray-400">empty</span>}
                          </code>
                        </div>
                      </td>
                      <td className="py-3">
                        {appManaged.includes(f) ? (
                          <span className="inline-flex items-center gap-2 text-gray-500 bg-gray-100 px-3 py-1 rounded-md">
                            <Lock className="h-4 w-4" /> 
                            App-managed
                          </span>
                        ) : (
                          <div className="flex items-center gap-4">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input 
                                type="radio" 
                                name={`pick-${f}`} 
                                checked={choice[f] === 'local'} 
                                onChange={() => setChoice(s => ({ ...s, [f]: 'local' }))}
                                className="text-blue-600"
                              />
                              <span className="text-sm">Keep Local</span>
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input 
                                type="radio" 
                                name={`pick-${f}`} 
                                checked={choice[f] === 'remote'} 
                                onChange={() => setChoice(s => ({ ...s, [f]: 'remote' }))}
                                className="text-blue-600"
                              />
                              <span className="text-sm">Use Server</span>
                            </label>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile view */}
          <div className="lg:hidden space-y-4">
            {fields.map(f => {
              const localVal = formatValue(local && local[f]);
              const remoteVal = formatValue(remote && remote[f]);
              const isDifferent = localVal !== remoteVal;
              
              return (
                <div key={f} className={`p-4 border rounded-lg ${isDifferent ? 'border-amber-300 bg-amber-50' : 'border-gray-200'}`}>
                  <div className="flex items-center gap-2 mb-3">
                    <h3 className="font-medium text-gray-900">{labels[f] || f}</h3>
                    {isDifferent && <div className="w-2 h-2 bg-amber-500 rounded-full" />}
                  </div>
                  
                  <div className="space-y-3 mb-4">
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Local</label>
                      <code className="block text-xs bg-gray-100 p-2 rounded break-all">
                        {localVal || <span className="text-gray-400">empty</span>}
                      </code>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">Server</label>
                      <code className="block text-xs bg-gray-100 p-2 rounded break-all">
                        {remoteVal || <span className="text-gray-400">empty</span>}
                      </code>
                    </div>
                  </div>

                  {appManaged.includes(f) ? (
                    <div className="inline-flex items-center gap-2 text-gray-500 bg-gray-100 px-3 py-2 rounded-md">
                      <Lock className="h-4 w-4" /> 
                      App-managed
                    </div>
                  ) : (
                    <div className="flex flex-col gap-2">
                      <label className="flex items-center gap-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                        <input 
                          type="radio" 
                          name={`pick-${f}`} 
                          checked={choice[f] === 'local'} 
                          onChange={() => setChoice(s => ({ ...s, [f]: 'local' }))}
                          className="text-blue-600"
                        />
                        <span>Keep Local Value</span>
                      </label>
                      <label className="flex items-center gap-3 p-3 border rounded-md cursor-pointer hover:bg-gray-50">
                        <input 
                          type="radio" 
                          name={`pick-${f}`} 
                          checked={choice[f] === 'remote'} 
                          onChange={() => setChoice(s => ({ ...s, [f]: 'remote' }))}
                          className="text-blue-600"
                        />
                        <span>Use Server Value</span>
                      </label>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Footer */}
        <footer className="p-6 border-t border-gray-200 bg-gray-50">
          <div className="flex justify-between items-center">
            <div className="text-sm text-gray-600">
              {Object.values(choice).filter(c => c === 'local').length} local, {' '}
              {Object.values(choice).filter(c => c === 'remote').length} server, {' '}
              {Object.values(choice).filter(c => c === 'locked').length} app-managed
            </div>
            <div className="flex gap-3">
              <button 
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50 transition-colors"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
                onClick={() => onResolve(buildResolved())}
              >
                <CheckCircle className="h-4 w-4" /> 
                Apply Resolution
              </button>
            </div>
          </div>
        </footer>
      </div>
    </div>
  );
}